import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { useAuth } from '../../context/AuthContext';
import { getCategoryName, setCategoryCache } from '../../utils/categoryNames';
import { useMessagesFilter } from '../../utils/useMessagesFilter';
import { MessagesFilterBar } from './MessagesFilterBar';
import { SellerName } from '../SellerName';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Single source of truth for the messages experience. Used by both
 * `pages/MessagesPage` (mode="full") and `pages/UserProfilePage` →
 * Messages tab (mode="embedded"). Owns:
 *   • thread + category fetching
 *   • filter state (Tous / Acheteur / Vendeur + category + sort)
 *   • loading and empty states
 *   • thread row UI
 *
 * Modes:
 *   • full     — full-width page layout with an h1 "Messages" title.
 *   • embedded — drops the page title so the component fits inside an
 *                existing layout (e.g. profile tab).
 *
 * `onUnreadChange(count)` (optional) — fires whenever the threads list
 * is loaded so callers can drive an external badge (e.g. profile-tab
 * unread count). Always reports the unread total of the *unfiltered*
 * threads — the badge should reflect actual unread messages, not what
 * the filter currently shows.
 */
export const MessagesList = ({ mode = 'full', onUnreadChange }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const filter = useMessagesFilter(threads);

  // ── Data fetching ────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    setLoading(true);

    Promise.all([
      axios.get(`${API}/messages/threads`, { withCredentials: true })
        .then((res) => res.data || [])
        .catch(() => {
          toast.error(t('common.loadFailed'));
          return [];
        }),
      axios.get(`${API}/categories`)
        .then((res) => res.data || [])
        .catch(() => []),
    ]).then(([threadsData, catsData]) => {
      if (cancelled) return;
      setThreads(threadsData);
      setCategories(catsData);
      setCategoryCache(catsData);
      const unread = threadsData.reduce((acc, th) => acc + (th.unread_count || 0), 0);
      if (typeof onUnreadChange === 'function') onUnreadChange(unread);
      setLoading(false);
    });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleThreadClick = (thread) => {
    navigate(`/messages/${thread.listing_id}/${thread.other_user_id}`);
  };

  const isFull = mode === 'full';
  const testIdPrefix = isFull ? 'messages' : 'profile-messages';
  const wrapperTestId = isFull ? 'messages-list-full' : 'messages-list-embedded';

  return (
    <div className="w-full" data-testid={wrapperTestId}>
      {isFull && (
        <h1
          className="text-lg md:text-xl font-medium text-text_strong tracking-wide mb-4"
          data-testid="messages-title"
        >
          Messages
        </h1>
      )}

      <MessagesFilterBar
        activeTab={filter.activeTab}
        setActiveTab={filter.setActiveTab}
        categoryFilter={filter.categoryFilter}
        setCategoryFilter={filter.setCategoryFilter}
        sortOrder={filter.sortOrder}
        setSortOrder={filter.setSortOrder}
        activeFilterCount={filter.activeFilterCount}
        categories={categories}
        testIdPrefix={testIdPrefix}
      />

      {loading ? (
        <div className="text-center py-12" data-testid={`${testIdPrefix}-loading`}>
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-text_muted">{t('common.loading')}</p>
        </div>
      ) : filter.filteredThreads.length === 0 ? (
        <div
          className="text-center py-12 bg-white border border-border rounded-md"
          data-testid={`${testIdPrefix}-empty`}
        >
          <p className="text-text_muted text-lg">{t('messages.noMessages')}</p>
          {threads.length === 0 && isFull && (
            <p className="text-sm text-text_muted mt-2">
              {t('messages.startConversationHint')}
            </p>
          )}
        </div>
      ) : (
        <div
          className="bg-white border border-border rounded-md divide-y divide-border"
          data-testid={isFull ? 'message-threads' : 'profile-messages-list'}
        >
          {filter.filteredThreads.map((thread) => (
            <div
              key={`${thread.listing_id}_${thread.other_user_id}`}
              onClick={() => handleThreadClick(thread)}
              className="p-4 hover:bg-surface cursor-pointer transition"
              data-testid={isFull ? `thread-${thread.listing_id}` : `profile-thread-${thread.listing_id}`}
            >
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  {thread.listing_image ? (
                    <img
                      src={`${API}/files/${thread.listing_image}`}
                      alt={thread.listing_title}
                      className="w-16 h-16 object-cover rounded-md"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-surface rounded-md flex items-center justify-center text-text_muted text-xs">
                      {t('common.noImage')}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start gap-2 mb-1">
                    <div className="flex-1 min-w-0 flex items-center gap-2">
                      <h3 className="font-semibold font-heading truncate" data-testid="thread-listing-title">
                        {thread.listing_title}
                      </h3>
                      {thread.listing_category && (
                        <span
                          className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-success-soft text-success border border-success max-w-[120px] truncate"
                          title={getCategoryName(thread.listing_category, t)}
                          data-testid="thread-listing-category"
                        >
                          {getCategoryName(thread.listing_category, t)}
                        </span>
                      )}
                    </div>
                    {thread.unread_count > 0 && (
                      <Badge className="bg-primary text-white flex-shrink-0" data-testid="thread-unread">
                        {thread.unread_count}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mb-2 min-w-0">
                    <Avatar className="w-6 h-6 flex-shrink-0">
                      <AvatarImage src={thread.other_user_picture} />
                      <AvatarFallback className="text-xs">
                        {thread.other_user_name?.charAt(0).toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <SellerName
                      user={{
                        name: thread.other_user_name,
                        display_name: thread.other_user_display_name,
                        seller_type: thread.other_user_seller_type,
                        business_name: thread.other_user_business_name,
                      }}
                      variant="inline"
                      badgeSize="mini"
                      primaryClassName="text-sm font-semibold text-text_main truncate"
                      testIdPrefix="thread-other"
                      className="min-w-0 flex-1"
                    />
                  </div>
                  <p className="text-sm text-text_muted truncate mb-1" data-testid="thread-last-message">
                    {thread.last_message}
                  </p>
                  <p className="text-xs text-text_muted">
                    {formatDistanceToNow(new Date(thread.last_message_time), { addSuffix: true })}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
